import { useEffect, useReducer } from "react";
import { getRuntimeConfig } from "./auth-core.runtime";
import { accessRequirementsFromConfig, decodeJwtPayload, evaluateAccess } from "./auth-access";
import { authGuardReducer, deriveGuardState, initialAuthGuardPhase } from "./auth-guard.reducer";
import type { AuthContextValue } from "./auth-core.types";
import { returnPathFrom } from "./auth-return";

/**
 * TODO(CPD-7): only "loading" | "granted" | "denied" | "failed" exist today — there's
 * no "pending" state for CPD-7's access-pending case (a valid, authenticated
 * user whose persona/role hasn't resolved yet). When it lands, add it as a
 * phase in ./auth-guard.reducer.ts, not as more logic in an effect below.
 * AccessDeniedPage's shell (src/pages/AccessDeniedPage) is meant to be
 * reused for an AccessPendingPage once this state exists — same shell,
 * different copy.
 *
 * "failed" is Auth0 reporting an error handling its callback (e.g. it returned
 * `?error=` to the app): a final phase in ./auth-guard.reducer.ts, shown and
 * never answered with another redirect, which would loop.
 */
export type AuthGuardState = "loading" | "granted" | "denied" | "failed";

/**
 * Orchestrates the async session/claim checks: owns the React lifecycle
 * (effects, cancellation) and dispatches events describing what happened.
 * The transitions between phases, and the redirect-once / claims-gating
 * invariants, live in the pure `authGuardReducer` (./auth-guard.reducer.ts);
 * the claim verdict itself comes from `evaluateAccess` (./auth-access.ts).
 */
export function useAuthGuardState(auth: AuthContextValue): AuthGuardState {
  const {
    isEnabled,
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    getAccessTokenSilently,
  } = auth;
  const [phase, dispatch] = useReducer(authGuardReducer, initialAuthGuardPhase);

  // Confirm the session, or discover it needs a silent-auth attempt / redirect.
  useEffect(() => {
    if (!isEnabled || isLoading) return;

    if (error) {
      dispatch({ type: "SIGN_IN_FAILED" });
      return;
    }

    if (isAuthenticated) {
      dispatch({ type: "SESSION_CONFIRMED" });
      return;
    }

    let cancelled = false;
    const returnTo = returnPathFrom(window.location);

    getAccessTokenSilently()
      .then(() => {
        if (!cancelled) dispatch({ type: "SESSION_CONFIRMED" });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: "SILENT_AUTH_FAILED", returnTo });
      });

    return () => {
      cancelled = true;
    };
  }, [isEnabled, isLoading, isAuthenticated, error, getAccessTokenSilently]);

  // Fire the redirect exactly once, the moment the reducer enters "redirecting".
  useEffect(() => {
    if (phase.name !== "redirecting") return;
    void loginWithRedirect({ appState: { returnTo: phase.returnTo } });
  }, [phase, loginWithRedirect]);

  // Once the session is confirmed, resolve the claim-based access decision.
  useEffect(() => {
    if (!isEnabled || phase.name !== "checking-claims") return;

    const requirements = accessRequirementsFromConfig(getRuntimeConfig());
    let cancelled = false;

    getAccessTokenSilently()
      .then((token: string) => {
        if (cancelled) return;
        dispatch({
          type: "CLAIMS_RESOLVED",
          decision: evaluateAccess(decodeJwtPayload(token), requirements),
        });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: "CLAIMS_RESOLVED", decision: "denied" });
      });

    return () => {
      cancelled = true;
    };
  }, [isEnabled, phase, getAccessTokenSilently]);

  if (!isEnabled) return "granted";
  return deriveGuardState(phase, isLoading);
}
